import { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import ProcessQuotationModal from '../../components/Sales/quotation/modal/ProcessQuotationModal';
import CustomerRegistrationPopup from '../../components/Customer/CustomerRegistrationPopup';
import BreadcrumbTwo from '../../components/Breadcrumbs/BreadcrumbTwo';
import SectionWrapper from '../../components/SectionWrapper/SectionWrapper';
import {
  CurrentOrderPanel,
  OrderMenuPanel,
  OrderMergeModal,
  OrderMobileStep,
  OrderModifierModal,
  OrderMoveItemsModal,
  OrderCustomerSelector,
  OrdersHeader,
  OrdersMobileNav,
  OrderSplitBillModal,
  OrderTablesPanel,
  OrderTableTransferModal,
} from '../../features/restaurant/orders';
import useApiErrorHandler from '../../hooks/useApiErrorHandler';
import { useAuthUser } from '../../hooks/auth/useAuthUser';
import { useCustomer } from '../../hooks/useCustomer';
import useRestaurantOrders from '../../hooks/restaurant/useRestaurantOrders';
import useRestaurantTableTheme from '../../hooks/restaurant/useRestaurantTableTheme';
import { RootState } from '../../store/store';
import WorkspaceLayout from '../../layout/WorkspaceLayout';
import BranchService from '../../services/branch/BranchService';
import RestaurantCatalogExtensionService from '../../services/restaurant/RestaurantCatalogExtensionService';

import { IBranchDropdown } from '../../types/branches/branchInterfaces';
import { RestaurantModifierGroupDTO } from '../../types/restaurant/RestaurantCatalogExtensionTypes';
import { RestaurantMenuItemDTO } from '../../types/restaurant/RestaurantMenuTypes';
import {
  activeModifierGroupsForMenuItem,
  restaurantItemPriceWithModifiers,
  validRestaurantModifierSelection,
} from '../../features/restaurant/orders/orderModifiers';
import { assignCreatedCustomerToOrder } from '../../features/restaurant/orders/assignCreatedCustomer';

const RestaurantOrders = () => {
  const [searchParams] = useSearchParams();
  const requestedOrderLoaded = useRef(false);

  const [branches, setBranches] = useState<IBranchDropdown[]>([]);
  const [branchId, setBranchId] = useState<number | ''>('');
  const [loadingBranches, setLoadingBranches] = useState(true);
  const [mobileStep, setMobileStep] = useState<OrderMobileStep>('TABLES');
  const [customerId, setCustomerId] = useState<number | ''>('');
  const [waiterId, setWaiterId] = useState<number | ''>('');
  const [notes, setNotes] = useState('');
  const [billingDialogOpen, setBillingDialogOpen] = useState(false);
  const [customerSelectorOpen, setCustomerSelectorOpen] = useState(false);
  const [transferOpen, setTransferOpen] = useState(false);
  const [moveItemsOpen, setMoveItemsOpen] = useState(false);
  const [mergeOpen, setMergeOpen] = useState(false);
  const [splitOpen, setSplitOpen] = useState(false);
  const [checkBillingId, setCheckBillingId] = useState<number | null>(null);
  const [modifierItem, setModifierItem] =
    useState<RestaurantMenuItemDTO | null>(null);
  const [modifierGroups, setModifierGroups] = useState<
    RestaurantModifierGroupDTO[]
  >([]);
  const [modifierIds, setModifierIds] = useState<number[]>([]);
  const [itemNotes, setItemNotes] = useState('');
  const [loadingModifiers, setLoadingModifiers] = useState(false);

  const orders = useRestaurantOrders(branchId);
  useRestaurantTableTheme(branchId);
  const { showRegistrationModal, openAddModal, closeAddModal, saveCustomer } =
    useCustomer();
  const { handle: handleApiError } = useApiErrorHandler();
  const { hasRole } = useAuthUser();
  const sessionEmail = useSelector((state: RootState) => state.auth.email);
  // Solo soporte puede asignar la orden a otro mesero; el resto la toma con su
  // usuario de sesión (el backend lo asigna cuando no se envía waiterId).
  const canChooseWaiter = Boolean(hasRole('SUPPORT'));
  // Mismo criterio que ya usa el resto del sistema para sucursal.
  const canPickBranch =
    hasRole('ADMIN') || hasRole('SUPPORT') || hasRole('ACCOUNTANT');

  // El cliente es obligatorio: se preselecciona el predeterminado en cuanto
  // llega el catálogo (o el primero si ninguno viene marcado).
  useEffect(() => {
    if (customerId !== '' || orders.customers.length === 0) return;
    const preferred =
      orders.customers.find((item) => item.defaultCustomer) ??
      orders.customers[0];
    if (preferred) setCustomerId(preferred.id);
  }, [orders.customers, customerId]);

  useEffect(() => {
    if (!canPickBranch) {
      // Rol operativo: siempre su propia sucursal, sin selector ni
      // posibilidad de sobreescribirla por query param.
      BranchService.fetchMyBranch()
        .then((mine) => {
          setBranches([mine]);
          setBranchId(mine.id);
        })
        .finally(() => setLoadingBranches(false));
      return;
    }

    BranchService.fetchDropdown()
      .then((data) => {
        setBranches(data);

        const requestedBranch = Number(searchParams.get('branchId'));

        if (data.length) {
          setBranchId(
            data.some((item) => item.id === requestedBranch)
              ? requestedBranch
              : data[0].id
          );
        }
      })
      .finally(() => setLoadingBranches(false));
  }, [searchParams, canPickBranch]);

  useEffect(() => {
    const orderId = Number(searchParams.get('orderId'));

    if (
      !branchId ||
      !orderId ||
      orders.loading ||
      !orders.tables.length ||
      requestedOrderLoaded.current
    ) {
      return;
    }

    requestedOrderLoaded.current = true;
    orders.openOrderById(orderId).then(() => setMobileStep('ORDER'));
  }, [branchId, orders.loading, orders.tables.length, searchParams]);

  useEffect(() => {
    setCustomerId('');
    setWaiterId('');
    setNotes('');
  }, [orders.selectedTable?.id]);

  const chooseMenuItem = async (item: RestaurantMenuItemDTO) => {
    setLoadingModifiers(true);

    try {
      const response =
        await RestaurantCatalogExtensionService.menuItemModifierGroups(item.id);
      const groups = activeModifierGroupsForMenuItem(response.data, item);

      if (!groups.length) {
        await orders.addItem({
          menuItemId: item.id,
          comboMenuId: null,
          modifierIds: [],
          quantity: 1,
          notes: null,
        });
        return;
      }

      setModifierGroups(groups);
      setModifierIds([]);
      setItemNotes('');
      setModifierItem(item);
    } catch (error) {
      handleApiError(error);
    } finally {
      setLoadingModifiers(false);
    }
  };

  const modifierValid = validRestaurantModifierSelection(
    modifierGroups,
    modifierIds
  );

  const toggleModifier = (group: RestaurantModifierGroupDTO, id: number) => {
    if (
      !group.active ||
      !group.modifiers.some(
        (modifier) =>
          modifier.id === id && modifier.active && modifier.groupId === group.id
      )
    ) {
      return;
    }

    if (modifierIds.includes(id)) {
      setModifierIds((current) => current.filter((value) => value !== id));
      return;
    }

    const groupSelected = group.modifiers.filter((modifier) =>
      modifierIds.includes(modifier.id)
    );

    if (groupSelected.length >= group.maxSelections) return;

    setModifierIds((current) => [...current, id]);
  };

  const modifierPreview = restaurantItemPriceWithModifiers(
    Number(modifierItem?.productPrice ?? 0),
    modifierGroups,
    modifierIds
  );

  const activeItems = useMemo(
    () => orders.order?.items?.filter((item) => item.status === 'ACTIVE') ?? [],
    [orders.order]
  );

  const canEdit =
    orders.order?.status === 'OPEN' &&
    (orders.order?.checks?.length ?? 0) === 0;

  useEffect(() => {
    if (
      !orders.order ||
      !['OPEN', 'CONFIRMED', 'IN_KITCHEN', 'READY'].includes(
        orders.order.status
      )
    ) {
      setCustomerSelectorOpen(false);
    }
  }, [orders.order?.id, orders.order?.status]);

  return (
    <WorkspaceLayout>
      <BreadcrumbTwo />

      <SectionWrapper>
        <OrdersHeader
          branches={branches}
          branchId={branchId}
          loadingBranches={loadingBranches}
          canPickBranch={canPickBranch}
          onBranchChange={setBranchId}
        />

        <OrdersMobileNav step={mobileStep} onStepChange={setMobileStep} />

        <div className="grid gap-4 bg-whiten p-4 dark:bg-boxdark-2 xl:grid-cols-[minmax(300px,0.85fr)_minmax(360px,1.2fr)_minmax(330px,0.95fr)]">
          <div
            className={`${
              mobileStep === 'TABLES' ? 'block' : 'hidden xl:block'
            } overflow-hidden rounded-xl border border-stroke bg-white shadow-default dark:border-strokedark dark:bg-boxdark`}
          >
            <OrderTablesPanel
              rooms={orders.rooms}
              tables={orders.tables}
              selectedRoomId={orders.selectedRoomId}
              selectedTable={orders.selectedTable}
              loading={
                orders.serviceType === 'DINE_IN'
                  ? orders.loading
                  : orders.tablelessLoading
              }
              disabled={!branchId}
              onRefresh={
                orders.serviceType === 'DINE_IN'
                  ? orders.load
                  : orders.refreshTablelessOrders
              }
              onRoomChange={orders.setSelectedRoomId}
              onSelectTable={(table) => {
                orders.selectTable(table);
                setMobileStep('ORDER');
              }}
              serviceType={orders.serviceType}
              onServiceTypeChange={orders.setServiceType}
              tablelessOrders={orders.tablelessOrders}
              tablelessLoading={orders.tablelessLoading}
              selectedOrderId={orders.order?.id ?? null}
              onSelectTablelessOrder={(orderId) => {
                orders.selectTablelessOrder(orderId);
                setMobileStep('ORDER');
              }}
              onStartTablelessOrder={() => {
                orders.startNewTablelessOrder();
                setMobileStep('ORDER');
              }}
            />
          </div>

          <div
            className={`${
              mobileStep === 'MENU' ? 'block' : 'hidden xl:block'
            } overflow-hidden rounded-xl border border-stroke bg-white shadow-default dark:border-strokedark dark:bg-boxdark`}
          >
            <OrderMenuPanel
              categories={orders.categories}
              menuItems={orders.menuItems}
              combos={orders.combos}
              selectedCategoryId={orders.selectedCategoryId}
              search={orders.menuSearch}
              filters={orders.menuFilters}
              menuLoading={orders.menuLoading}
              canEdit={canEdit}
              busy={orders.busy || loadingModifiers}
              onSearchChange={orders.setMenuSearch}
              onFiltersChange={orders.setMenuFilters}
              onCategoryChange={orders.setSelectedCategoryId}
              onChooseItem={chooseMenuItem}
              onChooseCombo={(combo) =>
                orders.addItem({
                  menuItemId: null,
                  comboMenuId: combo.id,
                  modifierIds: [],
                  quantity: 1,
                  notes: null,
                })
              }
            />
          </div>

          <div
            className={`${
              mobileStep === 'ORDER' ? 'block' : 'hidden xl:block'
            } overflow-hidden rounded-xl border border-stroke bg-white shadow-default dark:border-strokedark dark:bg-boxdark`}
          >
            <CurrentOrderPanel
              selectedTable={orders.selectedTable}
              order={orders.order}
              serviceType={orders.serviceType}
              customers={orders.customers}
              waiters={orders.waiters}
              customerId={customerId}
              waiterId={waiterId}
              canChooseWaiter={canChooseWaiter}
              sessionUserLabel={sessionEmail}
              notes={notes}
              activeItems={activeItems}
              busy={orders.busy}
              kitchenPrintStates={orders.kitchenPrintStates}
              loadingBilling={orders.loadingBilling}
              startingBilling={orders.startingBilling}
              billing={orders.billing}
              billingMessage={orders.billingMessage}
              onCustomerChange={setCustomerId}
              onOpenCustomerSelector={() => setCustomerSelectorOpen(true)}
              onWaiterChange={setWaiterId}
              onNotesChange={setNotes}
              onCreate={() =>
                orders.create(
                  customerId,
                  canChooseWaiter ? waiterId : '',
                  notes
                )
              }
              onCancelItem={(item) =>
                orders.cancelItem(
                  item.id,
                  item.menuItemName ||
                    item.itemName ||
                    item.productName ||
                    `Ítem #${item.menuItemId}`
                )
              }
              onUpdateQuantity={orders.updateQuantity}
              onUpdateComboQuantity={orders.updateComboQuantity}
              onCancelOrder={orders.cancelOrder}
              onConfirmOrder={orders.confirmOrder}
              onOpenMenu={() => setMobileStep('MENU')}
              kitchenWorkflowEnabled={orders.posFlags.kitchenWorkflowEnabled}
              onOpenBilling={async () => {
                // Sin flujo de cocina: la orden abierta se confirma (queda lista)
                // y se pasa directo a cobro.
                if (
                  !orders.posFlags.kitchenWorkflowEnabled &&
                  orders.order?.status === 'OPEN'
                ) {
                  const confirmed = await orders.confirmOrder();
                  if (!confirmed) return;
                }
                setBillingDialogOpen(true);
              }}
              onAddMore={async () => {
                const reopened = await orders.reopenOrder();
                if (reopened) setMobileStep('MENU');
              }}
              onContinueBilling={orders.continueBilling}
              posFlags={orders.posFlags}
              onTransferTable={() => setTransferOpen(true)}
              onMoveItems={() => setMoveItemsOpen(true)}
              onMergeOrders={() => setMergeOpen(true)}
              onSplitBill={() => setSplitOpen(true)}
            />
          </div>
        </div>
      </SectionWrapper>

      <ProcessQuotationModal
        isOpen={billingDialogOpen}
        quotationId={orders.order?.id ?? 0}
        loading={orders.startingBilling}
        onClose={() => setBillingDialogOpen(false)}
        onProcess={async (_orderId, documentTypeId) => {
          const started = await orders.startBilling(documentTypeId);

          if (started) setBillingDialogOpen(false);
        }}
        title="Cobrar orden"
        description="Seleccione el documento fiscal. El pago y la emisión se completarán en la caja existente."
        actionLabel="Ir a caja"
      />

      <OrderCustomerSelector
        open={customerSelectorOpen}
        customers={orders.customers}
        currentCustomerId={orders.order?.customerId ?? null}
        busy={orders.busy}
        onOpenChange={setCustomerSelectorOpen}
        onAddCustomer={openAddModal}
        onConfirm={async (nextCustomerId) => {
          const response = await orders.changeCustomer(nextCustomerId);
          if (response) setCustomerSelectorOpen(false);
        }}
      />

      <CustomerRegistrationPopup
        isOpen={showRegistrationModal}
        onClose={closeAddModal}
        onOpenChange={(open) => {
          if (!open) closeAddModal();
        }}
        onSubmit={async () => {
          const response = await assignCreatedCustomerToOrder({
            saveCustomer,
            refreshCustomers: orders.refreshCustomers,
            changeCustomer: orders.changeCustomer,
          });

          if (response) setCustomerSelectorOpen(false);
        }}
      />

      <OrderModifierModal
        item={modifierItem}
        groups={modifierGroups}
        selectedIds={modifierIds}
        notes={itemNotes}
        preview={modifierPreview}
        valid={modifierValid}
        busy={orders.busy || loadingModifiers}
        onToggleModifier={toggleModifier}
        onNotesChange={setItemNotes}
        onClose={() => setModifierItem(null)}
        onAdd={async () => {
          if (!modifierItem) return;

          await orders.addItem({
            menuItemId: modifierItem.id,
            comboMenuId: null,
            modifierIds,
            quantity: 1,
            notes: itemNotes.trim() || null,
          });

          setModifierItem(null);
        }}
      />

      <OrderTableTransferModal
        open={transferOpen}
        rooms={orders.rooms}
        currentTableId={orders.order?.tableId ?? null}
        busy={orders.busy}
        onOpenChange={setTransferOpen}
        onLoadTables={orders.loadFreeTables}
        onConfirm={async (targetTableId) => {
          const response = await orders.transferTable(targetTableId);
          if (response) setTransferOpen(false);
        }}
      />

      <OrderMoveItemsModal
        open={moveItemsOpen}
        rooms={orders.rooms}
        busy={orders.busy}
        onOpenChange={setMoveItemsOpen}
        onLoadItems={orders.loadOrderItems}
        onLoadTargets={orders.loadTransferTargets}
        onConfirm={async (targetOrderId, itemIds) => {
          const response = await orders.moveOrderItems(targetOrderId, itemIds);
          if (response) setMoveItemsOpen(false);
        }}
      />

      {orders.order && (
        <OrderMergeModal
          open={mergeOpen}
          currentOrder={{
            id: orders.order.id,
            orderNumber: orders.order.orderNumber ?? `#${orders.order.id}`,
            status: orders.order.status,
            tableId: orders.order.tableId ?? null,
            tableCode: orders.order.tableCode ?? null,
            tableName: orders.order.tableName ?? null,
            diningRoomId: orders.order.diningRoomId ?? null,
            diningRoomName: orders.order.diningRoomName ?? null,
            customerName: orders.order.customerName ?? null,
            waiterName: orders.order.waiterName ?? null,
            total: orders.order.total ?? 0,
            activeItemCount: activeItems.length,
          }}
          rooms={orders.rooms}
          busy={orders.busy}
          onOpenChange={setMergeOpen}
          onLoadTargets={orders.loadTransferTargets}
          onConfirm={async (targetOrderId, sourceOrderId) => {
            const response = await orders.mergeOrders(
              targetOrderId,
              sourceOrderId
            );
            if (response) setMergeOpen(false);
          }}
        />
      )}

      <OrderSplitBillModal
        open={splitOpen}
        order={orders.order}
        busy={orders.busy}
        onOpenChange={setSplitOpen}
        onSplit={async (checks) => {
          await orders.splitBill(checks);
        }}
        onClear={async () => {
          await orders.clearSplitBill();
        }}
        onBillCheck={(checkId) => {
          setSplitOpen(false);
          setCheckBillingId(checkId);
        }}
      />

      <ProcessQuotationModal
        isOpen={checkBillingId != null}
        quotationId={checkBillingId ?? 0}
        loading={orders.busy}
        onClose={() => setCheckBillingId(null)}
        onProcess={async (_orderId, documentTypeId) => {
          if (checkBillingId == null) return;
          const done = await orders.billCheck(checkBillingId, documentTypeId);
          if (done) setCheckBillingId(null);
        }}
        title="Cobrar subcuenta"
        description="Seleccione el documento fiscal. El pago y la emisión se completarán en la caja existente."
        actionLabel="Ir a caja"
      />
    </WorkspaceLayout>
  );
};

export default RestaurantOrders;
